import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// Definición de tipos para el usuario
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  jwtToken?: string;
  role: "admin" | "user" | "guest";
}

// Definición del estado global
interface AppState {
  // Estado del usuario
  user: User | null;
  isAuthenticated: boolean;

  // Estado del tema
  theme: "light" | "dark" | "system";

  // Estado de carga global
  isLoading: boolean;

  // Notificaciones o alertas
  notifications: Array<{
    id: string;
    type: "info" | "success" | "warning" | "error";
    message: string;
  }>;
}

// Definición de las acciones
interface AppActions {
  // Acciones de usuario
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;

  // Acciones de tema
  setTheme: (theme: "light" | "dark" | "system") => void;
  toggleTheme: () => void;

  // Acciones de carga
  setLoading: (isLoading: boolean) => void;

  // Acciones de notificaciones
  addNotification: (
    notification: Omit<AppState["notifications"][0], "id">,
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Acción de reset completo
  reset: () => void;
}

// Estado inicial
const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  theme: "system",
  isLoading: false,
  notifications: [],
};

// Crear el store con Zustand
export const useAppStore = create<AppState & AppActions>()(
  persist(
    (set, get) => ({
      // Estado inicial
      ...initialState,

      // Implementación de acciones
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      login: (user) =>
        set({
          user,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
        }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setTheme: (theme) => {
        set({ theme });

        // Aplicar el tema al documento
        if (theme === "dark") {
          document.documentElement.classList.add("dark");
        } else if (theme === "light") {
          document.documentElement.classList.remove("dark");
        } else {
          // System theme
          const isDark = window.matchMedia(
            "(prefers-color-scheme: dark)",
          ).matches;
          if (isDark) {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
        }
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === "light" ? "dark" : "light";
        get().setTheme(newTheme);
      },

      setLoading: (isLoading) => set({ isLoading }),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            ...state.notifications,
            {
              ...notification,
              id: `notification-${Date.now()}-${Math.random()}`,
            },
          ],
        })),

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      reset: () => set(initialState),
    }),
    {
      name: "app-storage", // Nombre del storage
      storage: createJSONStorage(() => localStorage), // Usar localStorage
      partialize: (state) => ({
        // Solo persistir estos campos
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        theme: state.theme,
      }),
    },
  ),
);
